import { isObject } from 'helpers/Object';
import get from 'lodash/get';
import intersection from 'lodash/intersection';
import isNil from 'lodash/isNil';
import { action } from 'mobx';
import { AuthService } from 'modules/auth';
import { AUTHORITY } from 'modules/auth/constants';
import { DefaultClassUtil } from 'modules/classManager';
import { CommentListService, CommunityStore as Store } from 'modules/community';
import {
  INDEXES_TABS,
  INITIAL_STATE,
  NOTIFICATION_STATUS
} from 'modules/community/constants';
import ContentPageService from 'modules/content/ContentPageService';
import { UiService } from 'modules/ui';

import './CommunityApi';

import { isString } from '../../helpers/String';
import { ROLES, VIEW_AS_PRODUCER_ROLES } from '../user/constants';

import Request from '@hotmart/request';

class CommunityService {
  @action
  redirectClassAcademy(comment) {
    if (!(comment || comment.page || this.viewAsProducerIsAllowed)) {
      return;
    }

    const classHash = comment.commentClass && comment.commentClass.id;

    ContentPageService.contentPreview(
      comment.page.id,
      ROLES.OWNER,
      null,
      classHash
    );
  }

  get viewAsProducerIsAllowed() {
    const hasPermission = AuthService.hasAuthorities(
      AUTHORITY.MEM_MANAGE_PRODUCT
    );

    const allowedUserRoles = intersection(
      VIEW_AS_PRODUCER_ROLES,
      AuthService.roles
    );

    return allowedUserRoles.length > 0 || hasPermission;
  }

  @action
  async getCommunityNotifications() {
    try {
      const data = await Request('notification.count');

      Store.setCommunityComments(data.count);

      return true;
    } catch (e) {
      UiService.handleError(e);

      return false;
    }
  }

  @action
  setData(data = {}) {
    Object.assign(Store, { ...data });
  }

  @action
  clear() {
    this.setData({ ...INITIAL_STATE });
  }

  @action
  async getTopicCommentsList(params = {}, commentUserId) {
    try {
      const response = await Request('get.history-comments', {
        ...params,
        commentUserId
      });

      this.setData(response);
    } catch (e) {
      UiService.handleError(e);

      return Promise.reject(e);
    }
  }

  async getCommentbyId(commentId) {
    try {
      const response = await Request('get.context-comment-by-id', {
        commentId
      });

      return response;
    } catch (e) {
      UiService.handleError(e);

      return false;
    }
  }

  @action
  async getCommentsList(params = {}) {
    try {
      const response = await Request('post.comments-filter', params);

      Store.setData({ ...params, ...response });
    } catch (e) {
      UiService.handleError(e);

      return Promise.reject(e);
    }
  }

  async getHistoryReply(parent, userId, params) {
    try {
      const response = await Request('post.comments-history-reply', {
        ...params,
        parent,
        userId
      });

      return response;
    } catch (error) {
      UiService.handleError(error);

      return false;
    }
  }

  async commentAnswerReply(userId, commentUserId, commentId, content = '') {
    try {
      const params = {
        userId,
        commentUserId,
        commentId,
        content
      };

      await Request('post.comment-answer-reply', params);

      return true;
    } catch (error) {
      UiService.handleError(error);

      return false;
    }
  }

  async commentStatusUpdate(status, commentUserId, commentId) {
    try {
      const params = { status, commentUserId, commentId };

      await Request('post.comment-status-update', params);

      return true;
    } catch (error) {
      UiService.handleError(error);

      return false;
    }
  }

  async commentStatusMassUpdate(commentsData) {
    try {
      await Request('post.comment-status-mass-update', commentsData);

      return true;
    } catch (error) {
      UiService.handleError(error);

      return false;
    }
  }

  async getNotificationMacros() {
    try {
      const response = await Request('get.support-list');

      return response;
    } catch (e) {
      UiService.handleError(e);

      return Promise.reject(e);
    }
  }

  async dispatchMacro(data) {
    try {
      await Request('post.comment-answer-macro', data);

      return true;
    } catch (error) {
      UiService.handleError(error);

      return false;
    }
  }

  async saveConfigurationData(options) {
    try {
      return Request('post.config-notifications', options);
    } catch (error) {
      UiService.handleError(error);

      return false;
    }
  }

  async getConfigurationData() {
    try {
      const response = await Request('get.config-notifications');

      this.setOptionsSnapshot(response);

      return response;
    } catch (error) {
      this.setOptionsSnapshot(INITIAL_STATE.optionsSnapshot);

      UiService.handleError(error);

      return false;
    }
  }

  async getImportedDateLast() {
    try {
      const response = await Request('get.support-imported-last');

      return response;
    } catch (error) {
      UiService.handleError(error);

      return false;
    }
  }

  async exportCommentsFile() {
    try {
      return await Request('get.support-export', {}, { responseType: 'blob' });
    } catch (error) {
      UiService.handleError(error);

      return false;
    }
  }

  async importCommentsFile(file) {
    const data = {
      tsv: file
    };

    try {
      await Request('post.support-import', data);

      return true;
    } catch (error) {
      UiService.handleError(error);

      return false;
    }
  }

  getUsernameEmail = (item) => {
    if (!item || !item.user) {
      return '';
    }

    const firstNameIndex = 0;
    const firstName = (item.user.name || '').split(' ')[firstNameIndex];
    const email = item.user.email ? ` (${item.user.email})` : '';

    return `${firstName}${email}`;
  };

  @action
  checkCommentByItem(item, checked) {
    Store.checkCommentByItem(item, checked);
  }

  @action
  checkCommentsBySelectAll() {
    Store.checkCommentsBySelectAll();
  }

  @action
  translateDefaultClassName() {
    const { comments } = Store;
    const translatedComments = comments.map(
      ({ commentClass = {}, ...otherProps }) => {
        const { name = '' } = commentClass;
        const translatedName = DefaultClassUtil.appendMarkToName(name);

        return {
          ...otherProps,
          commentClass: {
            ...commentClass,
            name: translatedName
          }
        };
      }
    );

    this.setData({ comments: translatedComments });
  }

  @action
  setPage(page) {
    Store.setPage(page);
  }

  @action
  setPerPage(perPage) {
    Store.setPerPage(perPage);
  }

  @action
  setOptionsSnapshot(optionsSnapshot) {
    Store.setOptionsSnapshot(optionsSnapshot);
  }

  setSelectAll(selectAll = false) {
    this.setData({ selectAll });
  }

  setDataTableParamsPagination(dataTableParams, page) {
    if (dataTableParams && dataTableParams.pagination) {
      const pagination = { ...dataTableParams.pagination, page };

      this.setData({ dataTableParams: { ...dataTableParams, pagination } });
    }
  }

  getNotificationStatus(activedTab) {
    return isNil(activedTab)
      ? this.status
      : NOTIFICATION_STATUS[INDEXES_TABS[activedTab]];
  }

  get classId() {
    return isObject(Store.membershipClass) ? Store.membershipClass.value : '';
  }

  get status() {
    const { status } = Store;

    if (isObject(status)) {
      return status.value;
    } else if (isString(status)) {
      return status;
    }

    return NOTIFICATION_STATUS.nonRevised;
  }

  get user() {
    return this.hasUserSelected ? Store.user.userEmail : Store.user;
  }

  get userObject() {
    return this.hasUserSelected && Store.user;
  }

  get hasUserSelected() {
    return isObject(Store.user) && Boolean(Store.user.userEmail);
  }

  get commentListParams() {
    const { dataTableParams, pageId, moduleId } = Store;
    const userAreaId = this.userObject.id;

    return {
      classId: this.classId,
      dataTableParams,
      status: this.getNotificationStatus(),
      userAreaId,
      pageId,
      moduleId
    };
  }

  get isPersonaEnabled() {
    return get(Store, 'optionsSnapshot.personaConfiguration.enabled', false);
  }

  get persona() {
    return get(Store, 'optionsSnapshot.personaConfiguration.persona', null);
  }

  @action
  resetCommentListParams(activedTab) {
    const classId = null;
    const defaultParams = { pagination: {} };
    const dataTableParams =
      CommentListService.initializeDataTableParams(defaultParams);
    const status = this.getNotificationStatus(activedTab);
    const userAreaId = null;
    const { pageId, pagesAndModules, moduleId } = INITIAL_STATE;

    this.setData({
      dataTableParams,
      membershipClass: classId,
      selectAll: false,
      status,
      user: userAreaId,
      pagesAndModules,
      pageId,
      moduleId
    });

    return {
      classId,
      dataTableParams,
      status,
      userAreaId,
      pagesAndModules,
      pageId,
      moduleId
    };
  }

  async getUserNameByCommentUserId(commentUserId) {
    try {
      const params = { commentUserId };

      const response = await Request(
        'get.user-name-by-comment-user-id',
        params
      );

      return response;
    } catch (error) {
      UiService.handleError(error);

      return false;
    }
  }
}

export default new CommunityService();
